import { useMemo } from 'react';
import type { LeagueBundle, PlayersFile } from '../../shared/types';
import { Avatar, PageHead, PositionTag } from '../components/common';
import { fmtRecord } from '../lib/format';
import { buildTeamViews, positionCounts, sortStandings, summarizePicks } from '../lib/league';
import { Link } from '../lib/router';
import { POSITION_ORDER } from '../lib/format';

export default function Teams({
  bundle,
  players,
}: {
  bundle: LeagueBundle;
  players: PlayersFile | null;
}) {
  const teams = useMemo(() => sortStandings(buildTeamViews(bundle)), [bundle]);

  return (
    <>
      <PageHead
        title="Teams"
        meta={[`${teams.length} rosters`, 'Positional depth and net pick capital']}
      />

      <div className="grid grid-2">
        {teams.map((t) => {
          const counts = positionCounts(t.team, players);
          const picks = summarizePicks(bundle.picks, t.roster_id);
          return (
            <Link to={`/teams/${t.roster_id}`} key={t.roster_id}>
              <article className="card card-pad" style={{ height: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <Avatar id={t.avatar} alt={t.name} large />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div
                      style={{
                        fontFamily: 'var(--display)',
                        fontSize: 21,
                        textTransform: 'uppercase',
                        lineHeight: 1.1,
                      }}
                    >
                      {t.name}
                    </div>
                    <div className="team-handle">@{t.manager}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div className="tabnum" style={{ fontWeight: 700 }}>
                      {fmtRecord(t.record)}
                    </div>
                    <div className="faint" style={{ fontSize: 11 }}>
                      {t.team.players.length} players
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    display: 'flex',
                    gap: 6,
                    marginTop: 14,
                    flexWrap: 'wrap',
                    alignItems: 'center',
                  }}
                >
                  {POSITION_ORDER.map((pos) => (
                    <span
                      key={pos}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                    >
                      <PositionTag position={pos} />
                      <span className="tabnum faint" style={{ fontSize: 12 }}>
                        {counts[pos] ?? 0}
                      </span>
                    </span>
                  ))}
                </div>

                <div
                  style={{
                    marginTop: 12,
                    paddingTop: 11,
                    borderTop: '1px solid var(--line-soft)',
                    display: 'flex',
                    gap: 8,
                    alignItems: 'center',
                    fontSize: 12,
                  }}
                >
                  <span className="faint">Draft capital</span>
                  <span
                    className={`pill ${picks.net > 0 ? 'pill-good' : picks.net < 0 ? 'pill-bad' : ''}`}
                  >
                    {picks.net > 0 ? '+' : ''}
                    {picks.net} picks
                  </span>
                  <span className="faint" style={{ marginLeft: 'auto' }}>
                    {picks.held.length} held
                  </span>
                </div>
              </article>
            </Link>
          );
        })}
      </div>
    </>
  );
}
